import { useCallback } from 'react';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { memoizeWeak } from '~/_shared/utils/memoize/memoize';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { isBoundaryTerritorySpecial } from '~/boundary/settings/boundarySettings.helpers';
import { useGetBoundaryTerritoryDisplayNames } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryTerritoryDisplayName';
import {
  type BoundaryId, type BoundaryTerritoryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import { type BoundaryStateItem } from '~/store/boundaryItems/boundaryItems.state';
import { getBoundaryLabelValueCallback } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.helpers';
import {
  type BoundaryTerritoryAssignment,
  type BoundaryTerritoryCountsAndExtraData,
  useBoundaryTerritoryAssignments,
  useBoundaryTerritoryExtraData,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { useActiveBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { type BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useMapComponentLastBoundsSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { hideBordersWhenBoundariesAsTerritories } from '../mapBoundary.helpers';
import {
  type MapBoundaryLabelLegendCallback, useMapBoundaryLabelLegend,
} from './useMapBoundaryLabelLegend';

export type MapBoundaryLabelContentCallback = (boundary: BoundaryStateItem) => {
  boundaryName: string | null;
  boundaryValue: string | null;
  boundaryValueLegend: string | null;
} | null;

const noContent = () => null;

export const useMapBoundaryLabelContent = () => {
  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();
  const boundaryTerritoryExtraData = useBoundaryTerritoryExtraData();
  const { boundaryTerritoryDisplayNames } = useGetBoundaryTerritoryDisplayNames();
  const { getMapBoundaryLabelLegend } = useMapBoundaryLabelLegend();
  const activeBoundaryTerritoryGroups = useActiveBoundaryTerritoryGroupsSelector();
  const lastBounds = useMapComponentLastBoundsSelector();
  const [t] = useTranslation();

  const createLabelContentResolver = useCallback((boundaryGroup: BoundaryGroup): MapBoundaryLabelContentCallback => {
    if (!lastBounds) {
      return noContent;
    }

    const boundaryTerritoryGroup = activeBoundaryTerritoryGroups.find(btg => btg.boundaryGroupId === boundaryGroup.id);

    if (!boundaryTerritoryGroup) {
      return noContent;
    }

    const hideLabelsWhenBoundariesAsTerritories = hideBordersWhenBoundariesAsTerritories(
      lastBounds.zoomLevel,
      boundaryGroup,
      boundaryTerritoryGroup
    );

    if (hideLabelsWhenBoundariesAsTerritories) {
      return noContent;
    }

    return getBoundaryLabelTextMemoized(
      boundaryTerritoryGroup,
      boundaryTerritoryAssignments.get(boundaryTerritoryGroup.boundaryTerritoryGroupId),
      boundaryTerritoryDisplayNames.get(boundaryTerritoryGroup.boundaryTerritoryGroupId),
      boundaryTerritoryExtraData.get(boundaryTerritoryGroup.boundaryTerritoryGroupId),
      getMapBoundaryLabelLegend,
      t,
    );
  }, [activeBoundaryTerritoryGroups, boundaryTerritoryAssignments, boundaryTerritoryDisplayNames,
    boundaryTerritoryExtraData, getMapBoundaryLabelLegend, lastBounds, t]);

  return { createLabelContentResolver };
};

const getBoundaryLabelText = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
  boundaryTerritoryAssignment: BoundaryTerritoryAssignment | undefined,
  boundaryTerritoryDisplayNames: ReadonlyMap<BoundaryTerritoryId, string> | undefined,
  boundaryExtraData: BoundaryTerritoryCountsAndExtraData | undefined,
  getMapBoundaryLabelLegend: MapBoundaryLabelLegendCallback,
  t: TranslationFnc,
) => {
  const { settings } = boundaryTerritoryGroup;
  const { labelValueConfig: { showValue, includeLegend }, hideLabels } = settings;

  // Get value callback based on boundary territory group settings
  const boundaryValueCallback = boundaryExtraData ? getBoundaryLabelValueCallback(boundaryTerritoryGroup, boundaryExtraData, t) : null;
  const boundaryValueLegend = getMapBoundaryLabelLegend(boundaryTerritoryGroup);

  const getTextFromBoundaryTerritory = (boundaryId: BoundaryId) => {
    const boundaryTerritoryId = boundaryTerritoryAssignment?.boundaryAssignments.get(boundaryId);
    const boundaryTerritory = settings.boundaryTerritories.find(bt => bt.boundaryTerritoryId === boundaryTerritoryId);

    if (!boundaryTerritory || isBoundaryTerritorySpecial(boundaryTerritory)) {
      return null;
    }

    return boundaryTerritoryDisplayNames?.get(boundaryTerritory.boundaryTerritoryId) ?? null;
  };

  return (boundary: BoundaryStateItem) => {
    const boundaryName = settings.boundariesAsTerritories ? getTextFromBoundaryTerritory(boundary.id) ?? boundary.displayName : boundary.displayName;
    const boundaryValue = boundaryValueCallback ? boundaryValueCallback(boundary.id) : null;

    return {
      boundaryName: hideLabels ? null : boundaryName,
      boundaryValue: showValue ? boundaryValue : null,
      boundaryValueLegend: includeLegend ? boundaryValueLegend : null,
    };
  };
};

const getBoundaryLabelTextMemoized = memoizeWeak(getBoundaryLabelText);
